import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { EmployeeSwitcher } from '@/components/composites/EmployeeSwitcher';
import { JiraWorklogTable } from '@/components/composites/JiraWorklogTable';
import { UnmatchedGroupsTable } from '@/components/composites/UnmatchedGroupsTable';
import { Badge } from '@/components/primitives/Badge';
import { EmptyState } from '@/components/primitives/EmptyState';
import { useEmployeeDetail } from '@/hooks/useEmployeeDetail';
import { currentDate, formatRelativeShort } from '@/lib/dates';
import type { EmployeeClientGroup, EmployeeProjectRow, AppUsageRow } from '@/data/employeeDetail';
import styles from './EmployeeDetailPage.module.css';

const JIRA_STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes — task 139 §Currency

function isJiraSyncStale(lastSyncedAt: string | null): boolean {
  if (!lastSyncedAt) return false; // Don't flag "never synced" as stale — that's the empty state.
  const then = Date.parse(lastSyncedAt);
  if (!Number.isFinite(then)) return false;
  return currentDate().getTime() - then > JIRA_STALE_THRESHOLD_MS;
}

function BarCell({ pct, muted }: { pct: number; muted?: boolean }) {
  return (
    <div className={styles.barCellWrap}>
      <div className={styles.barCell}>
        <div className={styles.barFill} style={{ width: `${pct}%`, opacity: muted ? 0.5 : 1 }} />
      </div>
      <span className={styles.barPct}>{pct.toFixed(1)}%</span>
    </div>
  );
}

function ClientProjectTable({ groups }: { groups: readonly EmployeeClientGroup[] }) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(groups.map((g) => [g.id, true])),
  );

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Sum hours and events across every project in every group. Collapsed
  // groups still contribute — the user's mental model is "this is the
  // total for this employee in this date range," not "this is the total
  // of what's currently visible."
  const totals = useMemo(
    () =>
      groups
        .flatMap((g) => g.projects)
        .reduce((acc, p) => ({ hours: acc.hours + p.hours, events: acc.events + p.events }), {
          hours: 0,
          events: 0,
        }),
    [groups],
  );

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Client / Project</th>
            <th>Type</th>
            <th className={styles.alignRight}>Hours</th>
            <th className={styles.alignRight}>Events</th>
            <th>% of Total</th>
          </tr>
        </thead>
        {groups.map((group: EmployeeClientGroup) => (
          <tbody key={group.id}>
            <tr className={styles.clientGroupRow} onClick={() => toggleGroup(group.id)}>
              <td>
                <span
                  className={`${styles.chevron} ${openGroups[group.id] ? styles.chevronOpen : ''}`}
                >
                  &#9654;
                </span>
                {group.isLink ? (
                  <Link
                    to={`/clients/${group.id}`}
                    className={styles.drillLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {group.name}
                    <span className={styles.drillArrow}>&rarr;</span>
                  </Link>
                ) : (
                  <span className={styles.drillLinkNoLink}>{group.name}</span>
                )}
              </td>
              <td />
              <td className={`${styles.mono} ${styles.alignRight}`}>{group.hours.toFixed(1)}h</td>
              <td className={`${styles.mono} ${styles.alignRight}`}>
                {group.events.toLocaleString()}
              </td>
              <td>
                <BarCell pct={group.pct} />
              </td>
            </tr>
            {openGroups[group.id] &&
              group.projects.map((p: EmployeeProjectRow) => (
                <tr key={p.name} className={styles.projectRow}>
                  <td className={styles.projectCell}>
                    <Link to="#" className={styles.drillLink}>
                      {p.name}
                      <span className={styles.drillArrow}>&rarr;</span>
                    </Link>
                  </td>
                  <td>
                    <Badge variant={p.type}>{p.type === 'billable' ? 'Billable' : 'General'}</Badge>
                  </td>
                  <td className={`${styles.mono} ${styles.alignRight}`}>{p.hours.toFixed(1)}h</td>
                  <td className={`${styles.mono} ${styles.alignRight}`}>
                    {p.events.toLocaleString()}
                  </td>
                  <td>
                    <BarCell pct={p.pct} />
                  </td>
                </tr>
              ))}
          </tbody>
        ))}
        <tfoot>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td />
            <td className={`${styles.mono} ${styles.alignRight}`}>{totals.hours.toFixed(1)}h</td>
            <td className={`${styles.mono} ${styles.alignRight}`}>
              {totals.events.toLocaleString()}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ReviewBanner({
  totalHours,
  totalSessions,
  reviewHref,
}: {
  totalHours: number;
  totalSessions: number;
  reviewHref: string;
}) {
  return (
    <div className={styles.reviewBanner}>
      <div className={styles.reviewBannerLeft}>
        <svg
          className={styles.reviewBannerIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div>
          <div className={styles.reviewBannerText}>
            {totalHours.toFixed(1)}h captured across {totalSessions.toLocaleString()} session
            {totalSessions === 1 ? '' : 's'} couldn&apos;t be matched to a project
          </div>
          <div className={styles.reviewBannerSub}>
            Kanjoy knows the application but needs your help identifying the client and project.
          </div>
        </div>
      </div>
      <Link to={reviewHref} className={styles.btnReviewAll}>
        Review all &rarr;
      </Link>
    </div>
  );
}

function AppUsageTable({ usage }: { usage: readonly AppUsageRow[] }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Application</th>
            <th className={styles.alignRight}>Hours</th>
            <th className={styles.alignRight}>% of Total</th>
            <th style={{ width: 200 }}>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {usage.map((app) => (
            <tr key={app.name}>
              <td className={app.muted ? styles.textMuted : undefined}>{app.name}</td>
              <td
                className={`${styles.mono} ${styles.alignRight} ${app.muted ? styles.textMuted : ''}`}
              >
                {app.hours.toFixed(1)}h
              </td>
              <td
                className={`${styles.mono} ${styles.alignRight} ${app.muted ? styles.textMuted : ''}`}
              >
                {app.pct.toFixed(1)}%
              </td>
              <td>
                <div className={styles.appBarTrack}>
                  <div
                    className={styles.appBarFill}
                    style={{ width: `${app.pct}%`, opacity: app.muted ? 0.5 : 1 }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td className={`${styles.mono} ${styles.alignRight}`}>
              {usage.reduce((s, a) => s + a.hours, 0).toFixed(1)}h
            </td>
            <td className={`${styles.mono} ${styles.alignRight}`}>100%</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ActivityChart({ bars, employeeId }: { bars: readonly number[]; employeeId: string }) {
  return (
    <div className={styles.activityWrap}>
      <div className={styles.activityChart}>
        {bars.map((h, i) => (
          <div key={`bar-${i}`} className={styles.activityBar} style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className={styles.activityLink}>
        {/* Object form (pathname + search) keeps the query string out of the
            route-integrity smoke test's template-literal regex, which normalizes
            the whole `to=` into a false-positive unmatched route. */}
        <Link
          to={{ pathname: '/timesheet/range', search: `?member=${employeeId}` }}
          className={styles.linkArrow}
        >
          View full timesheet &rarr;
        </Link>
      </div>
    </div>
  );
}

export function EmployeeDetailPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const {
    employee,
    stats,
    clientGroups,
    reviewItems,
    reviewItemsTotal,
    reviewTotalHours,
    reviewTotalSessions,
    appUsage,
    activityBars,
    jira,
    isLoading,
  } = useEmployeeDetail(employeeId);

  if (isLoading) {
    return (
      <>
        <PageHeader title="Loading\u2026" />
        <EmptyState title="Loading employee data\u2026" description="" />
      </>
    );
  }

  if (!employee) {
    return (
      <>
        <PageHeader title="Employee not found" />
        <EmptyState
          title="Employee not found"
          description="This employee does not exist in the current organization."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={employee.name}
        actions={
          <EmployeeSwitcher
            currentEmployeeId={employeeId ?? ''}
            onSwitch={(id) => navigate(`/employees/${id}`)}
          />
        }
      />

      <div className={styles.statGrid6}>
        {stats.map((s) => (
          <div key={s.label} className={styles.statCard}>
            <div className={styles.statLabel}>{s.label}</div>
            <div className={styles.statValue}>{s.value}</div>
            {s.sub && (
              <div className={`${styles.statSub} ${s.subMuted ? styles.statSubMuted : ''}`}>
                {s.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>Time by Client &amp; Project</h2>
      <ClientProjectTable groups={clientGroups} />

      {jira.enabled && (
        <>
          <h2 className={styles.sectionTitle}>
            Time in Jira
            {jira.lastSyncedAt && (
              <span className={styles.jiraSync}>
                Last synced {formatRelativeShort(jira.lastSyncedAt)}
                {isJiraSyncStale(jira.lastSyncedAt) && (
                  <span className={styles.jiraStalePill}>
                    <Badge variant="disabled">Stale</Badge>
                  </span>
                )}
              </span>
            )}
          </h2>
          {jira.groups.length > 0 ? (
            <JiraWorklogTable
              groups={jira.groups}
              showCredentialLabels={new Set(jira.groups.map((g) => g.credentialId)).size > 1}
            />
          ) : (
            <EmptyState title="No Jira worklogs in this range." description="" />
          )}
        </>
      )}

      {reviewItemsTotal > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Needs Your Review</h2>
          <ReviewBanner
            totalHours={reviewTotalHours}
            totalSessions={reviewTotalSessions}
            reviewHref={`/employees/${employee.id}/review`}
          />
          <UnmatchedGroupsTable
            items={reviewItems}
            mode="top-n"
            totalGroups={reviewItemsTotal}
            seeAllHref={`/employees/${employee.id}/review`}
            employeeId={employee.id}
          />
        </>
      )}

      <h2 className={styles.sectionTitle}>Time by Application</h2>
      <AppUsageTable usage={appUsage} />

      <h2 className={styles.sectionTitle}>Activity by Day</h2>
      <ActivityChart bars={activityBars} employeeId={employee.id} />
    </>
  );
}
